<!-- RetrieveStatusBadge.vue -->
<script lang="ts" setup>
import { computed } from 'vue';
import MessageBadge from '../../components/Messages/MessageBadge.vue';
import { assertInternally } from '../../lib/Environment';
import Icon from '../../components/Icon.vue';
import { DisplayStyle, IconAnimation, IconTypes } from '../../Enums/UiEnums';
import { IDocumentListItem } from './Models/IDocumentListItem';
import { DocumentRetrieveStatus } from './Models/DocumentRetrieveStatus';

    const props = defineProps<{
        doc:IDocumentListItem,
        title?: string
    }>();

    const documentRetrieveStatus: Record<DocumentRetrieveStatus, {badgeClass: string, userText: string, showSpinner?: boolean}> = {
        [DocumentRetrieveStatus.UNKNOWN]: {badgeClass: "UnknownStatus", userText: "Unknown"},
        [DocumentRetrieveStatus.Available]: {badgeClass: "AvailableStatus", userText: "Available"},
        [DocumentRetrieveStatus.Importing]: {badgeClass: "ImportingStatus", userText: "Importing", showSpinner: true },
        [DocumentRetrieveStatus.Imported]: {badgeClass: "ImportedStatus", userText: "Imported"},
        [DocumentRetrieveStatus.Retrieving]: {badgeClass: "RetrievingStatus", userText: "Retrieving", showSpinner: true },
        [DocumentRetrieveStatus.Retrieved]: {badgeClass: "RetrievedStatus", userText: "Retrieved"},
        [DocumentRetrieveStatus.Error]: {badgeClass: "ErrorStatus", userText: "Error"}
    };

    const documentStatus = computed<DocumentRetrieveStatus>(() => props.doc.retrieveStatus ?? DocumentRetrieveStatus.UNKNOWN);

    const documentRetrieveStatusText = computed<string|undefined>(() => {
        return documentRetrieveStatus[documentStatus.value].userText;
    });

    const documentRetrieveStatusOverrideStyle = computed<string|undefined>(() => {
        return `badge ${documentRetrieveStatus[documentStatus.value].badgeClass}`;
    });

    const showSpinner = computed<boolean>(() => {
        return documentRetrieveStatus[documentStatus.value].showSpinner ?? false;
    });

</script>

<style scoped>
    .badge {
        font-weight: normal;
        border-radius: 2px;
    }

    .UnknownStatus {
        background: lightgray;
        border: 2px solid darkgray;
        color:black;
    }

    /* WIP: Border colors don't match Figma perfectly due to a flaw in Figma/design implementation.  If not we'll need to invest more time.*/
    .AvailableStatus { 
        background: linear-gradient(0deg, rgba(220, 129, 129, .2), rgba(220, 129, 129, .2)), linear-gradient(0deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 1));
        border: 2px solid rgba(220, 129, 129, .5);        
        color:black;
    }

    .ImportingStatus { 
        background: linear-gradient(0deg, rgba(82, 161, 206, .2), rgba(82, 161, 206, .2)), linear-gradient(0deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 1));
        border: 2px solid rgba(82, 161, 206, .5);
        color:black;
    }

    .ImportedStatus { 
        background: linear-gradient(0deg, rgba(0, 0, 255, .2), rgba(0, 0, 255, .2)), linear-gradient(0deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 1));
        border: 2px solid rgba(0, 0, 255, .5);        
        color:black;
    }

    .RetrievingStatus { 
        background: linear-gradient(0deg, rgba(82, 161, 206, .2), rgba(82, 161, 206, .2)), linear-gradient(0deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 1));
        border: 2px solid rgba(82, 161, 206, .5);        
        color:black;
    }

    .RetrievedStatus { 
        background: linear-gradient(0deg, rgba(0, 158, 0, .2), rgba(0, 158, 0, .2)), linear-gradient(0deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 1));
        border: 2px solid rgba(0, 158, 0, .5);                
        color:black;
    }

    .ErrorStatus { 
        background: linear-gradient(0deg, rgba(255, 0, 0, .2), rgba(255, 0, 0, .2)), linear-gradient(0deg, rgba(255, 255, 255, 1), rgba(255, 255, 255, 1));
        border: 2px solid rgba(255, 0, 0, .5);        
        color:black;
    }
</style>

<template>
    <MessageBadge :overrideStyle="documentRetrieveStatusOverrideStyle" v-if="undefined != documentStatus" :title="props.title">
        {{ documentRetrieveStatusText ?? "Unknown" }}
        <Icon v-if="showSpinner" :icon="IconTypes.SpinnerDots" :animation="IconAnimation.spin" :displayStyle="DisplayStyle.Dark" />
    </MessageBadge>
</template>